import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Structured "checklist-style" list for Hebrew RTL content.
 *
 * Each row is a small card-row with a colored marker badge on the inline-end
 * (right in RTL) and the body text on the inline-start. Use for lists where
 * each item naturally starts with an emoji marker (✅ ❌ ⚡ 🎯 …) or a
 * `term:` definition prefix — turns markdown bullets into a UI that reads
 * like a checklist, not a wall of dots.
 *
 * The component is markup only; it does not parse markdown. The caller
 * (e.g. GuideMarkdown) decides how to derive `marker` and `body` from each
 * source line.
 */
export type HighlightListItem = {
  /** Short marker — emoji, glyph, or 1-3 char tag. Rendered in a colored badge. */
  marker?: ReactNode;
  /** Optional bold lead-in (the "term:" part, before the colon). */
  term?: ReactNode;
  /** Main body text or rich nodes. */
  body: ReactNode;
  /** Marker tone. Default chosen by inference at the call site. */
  tone?: HighlightTone;
  /** Stable id if the caller has one; else array index is used. */
  id?: string;
};

export type HighlightTone =
  | "emerald"
  | "rose"
  | "amber"
  | "blue"
  | "violet"
  | "stone";

const TONES: Record<
  HighlightTone,
  { badgeBg: string; badgeFg: string; ring: string }
> = {
  emerald: {
    badgeBg: "bg-emerald-50",
    badgeFg: "text-emerald-700",
    ring: "ring-emerald-200/70",
  },
  rose: {
    badgeBg: "bg-rose-50",
    badgeFg: "text-rose-700",
    ring: "ring-rose-200/70",
  },
  amber: {
    badgeBg: "bg-amber-50",
    badgeFg: "text-amber-700",
    ring: "ring-amber-200/70",
  },
  blue: {
    badgeBg: "bg-blue-50",
    badgeFg: "text-blue-700",
    ring: "ring-blue-200/70",
  },
  violet: {
    badgeBg: "bg-violet-50",
    badgeFg: "text-violet-700",
    ring: "ring-violet-200/70",
  },
  stone: {
    badgeBg: "bg-stone-100",
    badgeFg: "text-stone-700",
    ring: "ring-stone-200/70",
  },
};

export type HighlightListProps = {
  items: HighlightListItem[];
  /** Default tone applied to items that don't specify one. */
  defaultTone?: HighlightTone;
  /** Extra classes merged onto the outer list. */
  className?: string;
};

export function HighlightList({
  items,
  defaultTone = "stone",
  className,
}: HighlightListProps) {
  return (
    <ul className={cn("my-2 flex flex-col gap-2", className)} role="list">
      {items.map((item, idx) => {
        const tone = TONES[item.tone ?? defaultTone];
        return (
          <li
            key={item.id ?? idx}
            className={cn(
              "group flex items-start gap-3",
              "rounded-xl border border-stone-200/60 bg-white",
              "px-4 py-3",
              "shadow-[0_1px_2px_rgba(0,0,0,0.03)]",
              "transition-colors hover:border-stone-300/70",
            )}
          >
            {item.marker !== undefined ? (
              <span
                className={cn(
                  "shrink-0 inline-flex items-center justify-center",
                  "h-7 min-w-7 px-2 rounded-lg",
                  "text-sm font-semibold leading-none",
                  "ring-1 ring-inset",
                  tone.badgeBg,
                  tone.badgeFg,
                  tone.ring,
                )}
                aria-hidden="true"
              >
                {item.marker}
              </span>
            ) : null}

            <div className="min-w-0 flex-1 text-[15px] leading-[1.7] text-stone-800">
              {item.term ? (
                <span className="font-semibold text-stone-900 me-1.5">
                  {item.term}
                </span>
              ) : null}
              <span className="text-stone-700">{item.body}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
